import React, { createContext, useContext, useEffect, useState } from 'react';
import posthog, { PostHog } from 'posthog-js';
import { useAuthContext } from '@mono/auth-context';
import { usePlatStageContext } from '@mono/use-plat-stage';

import { postHogAPIKey, api_host, ui_host } from './config';
import { getIsSuperAdmin, getUser, organisationUtils } from './utils';
import { USER_TYPE } from './constants';

type App = 'accounts' | 'forecast' | 'hindcast' | 'particle_tracker' | 'dashboards' | 'dashboard_app' | 'weatherwidget_app';

const UserTrackingContext = createContext<{
    track: (event: string, properties?: any) => void;
    isUserTrackingLoaded: boolean;
    posthog: PostHog;
}>({
    track: () => {},
    isUserTrackingLoaded: false,
    posthog,
});

export const UserTrackingContextProvider = ({ app, children }: { app: App; children: JSX.Element }) => {
    const { stage } = usePlatStageContext();
    const { user, clients, metjwt } = useAuthContext();

    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        getIsSuperAdmin(stage).then((response) => setIsSuperAdmin(response));
    }, [metjwt, stage]);

    const invalidStage = stage !== 'prod';

    useEffect(() => {
        if (invalidStage) return;
        posthog
            .init(postHogAPIKey, {
                api_host,
                ui_host,
            })
            ?.onFeatureFlags(() => {
                setIsLoaded(true);
            });
    }, []);

    useEffect(() => {
        if (!user) return;
        const organisation = clients.find((client) => client.id === user.clientId);
        if (!organisation) return;
        (async () => {
            const response = await getUser(stage);

            const { isMetservice, getUserType, getOrganisationType } = organisationUtils(organisation.id);

            posthog.identify(user.userId, {
                staff: isMetservice(),
                user_id: user.userId,
                user_type: isSuperAdmin ? USER_TYPE.SUPER_ADMIN : getUserType(),
                user_email: user.email,
                user_created_at: response?.data.createdAt,
                organisation_name: organisation.name,
                organisation_id: organisation.id,
                organisation_created_at: organisation.createdAt,
                organisation_starts_at: organisation.startsAt,
                organisation_end_at: organisation.endsAt,
                organisation_type: getOrganisationType(),
            });
        })();
    }, [user?.userId, user?.clientId, clients.join(''), isSuperAdmin]);

    const track = (event: string, properties?: any) => {
        if (invalidStage || !isLoaded) return;
        posthog.capture(event, { ...properties, app });
    };

    return <UserTrackingContext.Provider value={{ track, isUserTrackingLoaded: isLoaded, posthog }}>{children}</UserTrackingContext.Provider>;
};

export const useUserTracking = () => useContext(UserTrackingContext);
